/**
 * @fileoverview View User profile.
 * @description The read-only profile in the spec's sections: Contact Details (employee ID, name,
 * email, mobile), Organisation Details (role, department, state, reporting manager), Account
 * Status (status, date of joining) and Assigned Banks (a bank the user owns, or one proposed to
 * them). A deactivated user's retained manager is shown with a note that the line has ended.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import {
  DIRECTORY_STATUS_LABEL_KEY,
  DIRECTORY_STATUS_VARIANT,
  formatJoiningDate,
  formatPhone,
  fullName,
} from '@/features/users/utils/user-display.utils'
import Badge from '@/shared/components/atoms/Badge'
import DetailField from '@/shared/components/molecules/DetailField'
import DetailSectionHeading from '@/shared/components/molecules/DetailSectionHeading'

import type { AssignedBank, UserDetail } from '@/features/users/types/users.types'

/** How a bank relates to the user, as its translation key. */
const BANK_RELATION_LABEL_KEY: Record<AssignedBank['relation'], string> = {
  PROSPECT_OWNER: 'sales.userManagement.viewUser.bankRelation.PROSPECT_OWNER',
  PROPOSED_PROSPECT_OWNER: 'sales.userManagement.viewUser.bankRelation.PROPOSED_PROSPECT_OWNER',
}

/**
 * @description Renders the Contact Details section.
 * @param {{ user: UserDetail }} props - The user.
 */
function ContactSection({ user }: { user: UserDetail }) {
  const { t } = useTranslation()

  return (
    <section className="flex flex-col gap-2.5">
      <DetailSectionHeading>
        {t('sales.userManagement.viewUser.sections.contactDetails')}
      </DetailSectionHeading>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <DetailField
          label={t('sales.userManagement.viewUser.fields.id')}
          data-testid="view-user-id"
        >
          {user.employeeCode ?? '—'}
        </DetailField>
        <DetailField
          label={t('sales.userManagement.viewUser.fields.name')}
          data-testid="view-user-name"
        >
          {fullName(user)}
        </DetailField>
        <DetailField
          label={t('sales.userManagement.viewUser.fields.email')}
          data-testid="view-user-email"
        >
          {user.email}
        </DetailField>
        <DetailField
          label={t('sales.userManagement.viewUser.fields.phone')}
          data-testid="view-user-phone"
        >
          {formatPhone(user)}
        </DetailField>
      </div>
    </section>
  )
}

/**
 * @description Renders the Organisation Details section.
 * @param {{ user: UserDetail }} props - The user.
 */
function OrganisationSection({ user }: { user: UserDetail }) {
  const { t } = useTranslation()

  return (
    <section className="flex flex-col gap-2.5">
      <DetailSectionHeading>
        {t('sales.userManagement.viewUser.sections.organisationDetails')}
      </DetailSectionHeading>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <DetailField
          label={t('sales.userManagement.viewUser.fields.role')}
          data-testid="view-user-role"
        >
          {user.primaryRole?.name ?? '—'}
        </DetailField>
        <DetailField
          label={t('sales.userManagement.viewUser.fields.dept')}
          data-testid="view-user-department"
        >
          {user.department?.name ?? '—'}
        </DetailField>
        <DetailField
          label={t('sales.userManagement.viewUser.fields.state')}
          data-testid="view-user-state"
        >
          {user.state?.name ?? t('sales.userManagement.viewUser.allIndia')}
        </DetailField>
        <DetailField
          label={t('sales.userManagement.viewUser.fields.reportsTo')}
          data-testid="view-user-reporting-manager"
        >
          {user.reportingManager?.displayName ?? '—'}
          {user.reportingManager?.reportingLineStatus === 'ENDED' && (
            <span
              className="mt-0.5 block text-[12px] font-normal text-slate"
              data-testid="view-user-reporting-manager-ended-note"
            >
              {t('sales.userManagement.viewUser.retainedManagerNote')}
            </span>
          )}
        </DetailField>
      </div>
    </section>
  )
}

/**
 * @description Renders the Account Status section.
 * @param {{ user: UserDetail }} props - The user.
 */
function StatusSection({ user }: { user: UserDetail }) {
  const { t } = useTranslation()
  const joiningDate =
    formatJoiningDate(user.dateOfJoining) ||
    // Only an invitee is waiting to join; anyone else simply has no date on file.
    (user.directoryStatus === 'PENDING'
      ? t('sales.userManagement.viewUser.fields.dojPending')
      : '—')

  return (
    <section className="flex flex-col gap-2.5">
      <DetailSectionHeading>
        {t('sales.userManagement.viewUser.sections.accountStatus')}
      </DetailSectionHeading>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <DetailField
          label={t('sales.userManagement.viewUser.fields.status')}
          data-testid="view-user-status"
        >
          <Badge variant={DIRECTORY_STATUS_VARIANT[user.directoryStatus]} dot>
            {t(DIRECTORY_STATUS_LABEL_KEY[user.directoryStatus])}
          </Badge>
        </DetailField>
        <DetailField
          label={t('sales.userManagement.viewUser.fields.doj')}
          data-testid="view-user-joining-date"
        >
          {joiningDate}
        </DetailField>
      </div>
    </section>
  )
}

/**
 * @description Renders the Assigned Banks section: each bank with whether the user owns it or has
 * it proposed to them.
 * @param {{ user: UserDetail }} props - The user.
 */
function AssignedBanksSection({ user }: { user: UserDetail }) {
  const { t } = useTranslation()

  return (
    <section className="flex flex-col gap-2.5">
      <DetailSectionHeading>
        {t('sales.userManagement.viewUser.sections.assignedBanks')}
      </DetailSectionHeading>
      {user.assignedBanks.length === 0 ? (
        <p className="text-[13px] text-slate" data-testid="view-user-no-banks">
          {t('sales.userManagement.viewUser.noAssignedBanks')}
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5" data-testid="view-user-banks">
          {user.assignedBanks.map((bank) => (
            <li
              key={bank.prospectId}
              data-testid={`view-user-bank-${bank.prospectId}`}
              className="flex items-center justify-between gap-3 text-[13px] text-ink"
            >
              <span>{bank.bankName}</span>
              <Badge variant={bank.relation === 'PROSPECT_OWNER' ? 'success' : 'warning'}>
                {t(BANK_RELATION_LABEL_KEY[bank.relation])}
              </Badge>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

/**
 * @component
 * @param {{ user: UserDetail }} props - The user.
 * @param {UserDetail} props.user - The user whose profile to show.
 */
function ViewUserProfile({ user }: { user: UserDetail }) {
  return (
    <>
      <ContactSection user={user} />
      <OrganisationSection user={user} />
      <StatusSection user={user} />
      <AssignedBanksSection user={user} />
    </>
  )
}

export default ViewUserProfile
